import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { MdoReportAdvice } from '@common/customControls/bariatrics';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import type {
    ConclusieAanvullend,
    ConclusieAanvullendReadKey,
    ConclusieAanvullendReadLabel,
    IndicatieVerwijzing,
    IndicatieVerwijzingReadKey,
    IndicatieVerwijzingSectionTitle,
    IndicatieVoorVerwijzingReadLabel,
} from './../../utils';
import { conclusieAanvullendSectionItems, indicatieVerwijzingReadSection } from './../../utils';

export type BariPreResumeMdoData = ConclusieAanvullend &
    IndicatieVerwijzing & {
        hervatten: Exclude<MdoReportAdvice, MdoReportAdvice.notYet> | null;
        toelichting: string | null;
    };

export type BariPreResumeMdoReadKey =
    | keyof BariPreResumeMdoData
    | ConclusieAanvullendReadKey
    | IndicatieVerwijzingReadKey;
export type BariPreResumeMdoReadLabel =
    | ConclusieAanvullendReadLabel
    | 'Hervatten in zorgpad'
    | IndicatieVoorVerwijzingReadLabel
    | 'Toelichting';
export type BariPreResumeMdoSectionTitle = 'Conclusie' | IndicatieVerwijzingSectionTitle;

export const bariPreResumeMdoReadSchema: TypedReadSchema<
    BariPreResumeMdoReadKey,
    BariPreResumeMdoReadLabel,
    BariPreResumeMdoSectionTitle
>[] = [
    {
        items: [
            { key: 'hervatten', label: 'Hervatten in zorgpad' },
            { key: 'toelichting', label: 'Toelichting' },
            ...conclusieAanvullendSectionItems,
        ],
        sectionTitle: 'Conclusie',
    },
    { ...indicatieVerwijzingReadSection },
];

const bariPreResumeMdoFinal: TypedSchema<BariPreResumeMdoData> = {
    conclusie_aanvullend_diabetes_mellitus: Koi.boolean(),
    conclusie_aanvullend_hydrochloorthiazide: Koi.boolean(),
    conclusie_aanvullend_psychofarmaca: Koi.boolean(),
    conclusie_aanvullend_roken: Koi.boolean(),
    hervatten: Koi.valid(...Object.values(MdoReportAdvice).filter((advice) => advice !== MdoReportAdvice.notYet)),
    indicatie_voor_verwijzing_diabetes: Koi.boolean(),
    indicatie_voor_verwijzing_hartfalen: Koi.boolean(),
    indicatie_voor_verwijzing_hypertensie: Koi.boolean(),
    indicatie_voor_verwijzing_medicatie: Koi.boolean(),
    indicatie_voor_verwijzing_nefrologie: Koi.boolean(),
    indicatie_voor_verwijzing_obesitas_specifiek: Koi.boolean(),
    indicatie_voor_verwijzing_osteoporose: Koi.boolean(),
    indicatie_voor_verwijzing_overig: Koi.boolean(),
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema('indicatie_voor_verwijzing_overig'),
    toelichting: getTextInputStringSchema(),
};

const bariPreResumeMdoDraft: TypedSchema<BariPreResumeMdoData> = addAllowNull({
    ...bariPreResumeMdoFinal,
    indicatie_voor_verwijzing_overig_toelichting: getTextAreaConditionalSchema(
        'indicatie_voor_verwijzing_overig',
        true
    ),
});

export const bariPreResumeMdoDraftSchema = Koi.object(bariPreResumeMdoDraft);
export const bariPreResumeMdoFinalSchema = Koi.object(bariPreResumeMdoFinal);
